import {
  NgxMatDateFormats,
  NGX_MAT_DATE_FORMATS,
} from '@angular-material-components/datetime-picker';
import { FieldType } from '@ngx-formly/material';
import { isDate } from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { LocaleService } from '@lib/translation';
import { COLORS } from '@lib/ui/theme';

import { ISnDateTimePickerFormFieldConfig } from '../../interfaces/date-time-picker-form-field-config.interface';
import { ISnDateTimePickerFieldTemplateOptions } from '../../interfaces/date-time-picker-form-field-template-option.interfaces';

export function getDateTimeFormatsFactory(localeService: LocaleService): NgxMatDateFormats {
  const dateInputFormat = localeService.getDateInputFormat();
  
  return {
    parse: {
      dateInput: dateInputFormat,
    },
    display: {
      dateInput: dateInputFormat,
      monthYearLabel: 'MMM YYYY',
      dateA11yLabel: 'LL',
      monthYearA11yLabel: 'MMMM YYYY',
    },
  };
}

@Component({
  selector: 'sn-date-time-picker-form-field',
  templateUrl: './date-time-picker.form-field.html',
  styleUrls: ['./date-time-picker.form-field.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NGX_MAT_DATE_FORMATS,
      useFactory: getDateTimeFormatsFactory,
      deps: [LocaleService],
    },
  ],
})
export class SnDateTimePickerFormField
  extends FieldType<ISnDateTimePickerFormFieldConfig>
  implements OnInit, OnDestroy
{
  public darkTheme = {
    container: {
      bodyBackgroundColor: COLORS['base-800'],
      buttonColor: COLORS['accent-600'],
    },
    dial: {
      dialBackgroundColor: COLORS['base-800'],
    },
    clockFace: {
      clockFaceBackgroundColor: COLORS['base-800'],
      clockHandColor: COLORS['accent-600'],
      clockFaceTimeInactiveColor: '#fff',
    },
  };

  public defaultOptions: ISnDateTimePickerFormFieldConfig = {
    type: 'datetimepicker',
    templateOptions: {},
  };

  public dateControl = new UntypedFormControl();
  public minDate: Date;
  public maxDate: Date;
  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  public ngOnInit(): void {
    if (this.templateOptions.minDate) {
      this.minDate = this.templateOptions.minDate();
    }

    if (this.templateOptions.maxDate) {
      this.maxDate = this.templateOptions.maxDate();
    }

    this.dateControl.setValue(this.formControl.value);

    this.dateControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      if (value && !isDate(value)) {
        this.formControl.setValue(value.toDate());
      }
    });

    this.formControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      if (this.to.change) {
        this.to.change(this.field, value);
      }

      if (this.templateOptions.minDate) {
        this.minDate = this.templateOptions.minDate();
      }

      if (this.templateOptions.maxDate) {
        this.maxDate = this.templateOptions.maxDate();
      }

      this._changeDetectorRef.markForCheck();
    });
  }

  public updateMinMax(): void {
    if (this.templateOptions.minDate) {
      this.minDate = this.templateOptions.minDate();
    }

    if (this.templateOptions.maxDate) {
      this.maxDate = this.templateOptions.maxDate();
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get templateOptions(): ISnDateTimePickerFieldTemplateOptions {
    return this.field.templateOptions;
  }
}
